/**
 * Studio API origin for the web SPA. Empty = same-origin (local).
 * Added: 2026-10-01.
 */

let originOverride: string | null = null;

export function setStudioApiOriginOverride(url: string | null): void {
  originOverride = url === null ? null : url.trim().replace(/\/+$/, '');
}

export function getStudioApiOrigin(): string {
  if (originOverride !== null) {
    return originOverride;
  }
  const fromWindow = readOriginFromWindow();
  if (fromWindow !== '') {
    return fromWindow;
  }
  return '';
}

export function studioStageOrigin(): string {
  return (
    getStudioApiOrigin() ||
    (typeof window !== 'undefined' ? window.location.origin : '')
  );
}

export function studioApiUrl(path: string): string {
  const origin = getStudioApiOrigin();
  const [rawPath, query] = path.split('?');
  const withSlash = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  const prefixed = withSlash.startsWith('/api/')
    ? withSlash
    : `/api/v1${withSlash}`;
  const url = origin ? `${origin}${prefixed}` : prefixed;
  return query ? `${url}?${query}` : url;
}

/** Iframe src on a remote studio origin: pass SPA origin for postMessage. */
export function withStudioParentOrigin(url: string): string {
  const studioOrigin = getStudioApiOrigin();
  if (!studioOrigin || typeof window === 'undefined') {
    return url;
  }
  const parent = window.location.origin;
  if (!parent || parent === studioOrigin) {
    return url;
  }
  try {
    const parsed = new URL(url, parent);
    parsed.searchParams.set('parent', parent);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function isStudioRemoteApiUrl(url?: string): boolean {
  if (!url || getStudioApiOrigin() === '') {
    return false;
  }
  // Strip origin + query so me/credit-transactions/studio-projects/… stays on web.
  const path = url
    .replace(/^https?:\/\/[^/?#]+/i, '')
    .replace(/\?.*$/, '')
    .replace(/^\/+/, '');
  if (
    path === 'api/v1/studio/handoff' ||
    path.startsWith('api/v1/studio/handoff/')
  ) {
    return false;
  }
  return (
    path === 'api/v1/studio-projects' ||
    path.startsWith('api/v1/studio-projects/') ||
    path.startsWith('api/v1/studio/ai') ||
    path.startsWith('api/v1/studio/session') ||
    path === 'api/v1/logs/studio-jobs' ||
    path.startsWith('api/v1/logs/studio-jobs/') ||
    path === 'api/v1/studio-roadmap/votes' ||
    path.startsWith('api/v1/studio-roadmap/votes/')
  );
}

function readOriginFromWindow(): string {
  if (typeof window === 'undefined' || window.bootstrapData == null) {
    return '';
  }
  try {
    const raw = window.bootstrapData as unknown;
    const data =
      typeof raw === 'string'
        ? (JSON.parse(raw) as {settings?: {studio_api_url?: string}})
        : (raw as {settings?: {studio_api_url?: string}});
    const url = data?.settings?.studio_api_url;
    if (typeof url !== 'string') {
      return '';
    }
    return url.trim().replace(/\/+$/, '');
  } catch {
    return '';
  }
}

export function appendWsTicket(wsPath: string, ticket: string): string {
  if (/^wss?:\/\//i.test(wsPath) || /^https?:\/\//i.test(wsPath)) {
    const absolute = wsPath.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    const url = new URL(absolute);
    url.searchParams.set('ticket', ticket);
    return url.toString();
  }
  const protocol =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? 'wss:'
      : 'ws:';
  const host =
    typeof window !== 'undefined' ? window.location.host : 'localhost';
  const path = wsPath.startsWith('/') ? wsPath : `/${wsPath}`;
  const sep = path.includes('?') ? '&' : '?';
  return `${protocol}//${host}${path}${sep}ticket=${encodeURIComponent(ticket)}`;
}
